import { Box, Stack } from '@mui/material'
import { UserEditField } from 'components/shared/UserEditField'
import AvatarUploader from 'components/settings/components/setting-users/components/avatar-uploader/AvatarUploader'
import { useCreateMilkerMutation } from 'hook/mutations/useCreateMilkerMutation'
import { useUpdateMilkerMutation } from 'hook/mutations/useUpdateMilkerMutation'
import { FC, useEffect, useMemo, useState } from 'react'
import { ButtonUI } from 'ui/buttons/ButtonUI'
import Drawer from 'ui/Drawer'

interface AddEmployeeDrawerProps {
	open: boolean
	onClose: () => void
	employee?: { id: number | string; fullName: string; avatar: string | null }
}

export const AddEmployeeDrawer: FC<AddEmployeeDrawerProps> = ({
	open,
	onClose,
	employee,
}) => {
	const isEdit = Boolean(employee)
	const [fullname, setFullname] = useState('')
	const [isNameFocused, setIsNameFocused] = useState(false)
	const [avatar, setAvatar] = useState<File | null>(null)
	const [isAvatarChanged, setIsAvatarChanged] = useState(false)
	const createMilker = useCreateMilkerMutation()
	const updateMilker = useUpdateMilkerMutation()

	const initialFullname = useMemo(() => employee?.fullName ?? '', [employee])

	useEffect(() => {
		if (!open) return
		setFullname(initialFullname)
		setAvatar(null)
		setIsAvatarChanged(false)
		setIsNameFocused(false)
	}, [open, initialFullname])

	const nameFieldLabel =
		!isEdit && !isNameFocused && !fullname.trim()
			? 'Введите фамилию и имя'
			: 'Фамилия Имя'

	const handleAvatarChange = (file: File | null) => {
		setAvatar(file)
		setIsAvatarChanged(true)
	}

	const isNameChanged = fullname.trim() !== initialFullname.trim()
	const isEditDirty = isNameChanged || isAvatarChanged
	const isPending = createMilker.isPending || updateMilker.isPending
	const isSubmitDisabled =
		!fullname.trim() || isPending || (isEdit ? !isEditDirty : false)

	const handleSubmit = () => {
		if (isEdit && employee) {
			updateMilker.mutate(
				{
					id: employee.id,
					fullName: fullname.trim(),
					photo: isAvatarChanged ? avatar : undefined,
				},
				{
					onSuccess: () => {
						onClose()
					},
				},
			)
			return
		}

		createMilker.mutate(
			{ fullName: fullname.trim(), photo: avatar },
			{
				onSuccess: () => {
					setFullname('')
					setAvatar(null)
					setIsAvatarChanged(false)
					onClose()
				},
			},
		)
	}

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title={isEdit ? 'Редактировать дояра' : 'Добавить дояра'}
			anchor='right'
		>
			<Stack
				direction='column'
				gap='16px'
				margin='0px 24px'
				sx={{ minHeight: '100%' }}
			>
				<AvatarUploader
					key={employee?.id ? `${employee.id}-${String(open)}` : String(open)}
					initialAvatar={employee?.avatar ?? undefined}
					onChange={handleAvatarChange}
				/>
				<UserEditField
					label={nameFieldLabel}
					value={fullname}
					width='100%'
					onChange={e => setFullname(e.target.value)}
					onFocus={() => setIsNameFocused(true)}
					onBlur={() => setIsNameFocused(false)}
				/>
				<Box sx={{ mt: 'auto', pb: '24px' }}>
					<ButtonUI
						onClick={handleSubmit}
						disabled={isSubmitDisabled}
						sx={{ height: '56px', width: '100%' }}
					>
						{isEdit ? 'Сохранить' : 'Создать'}
					</ButtonUI>
				</Box>
			</Stack>
		</Drawer>
	)
}
